import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { LoginInput, RegisterInput } from '@eriko/validation';
import { getAuthMe, login, register, logout, forgotPassword, resetPassword, verifyEmail } from '../api/auth';
import { getConversations } from '../api/conversations';
import { useAuthStore } from '../stores/authStore';

/** Validate current session by calling /auth/me */
export function useAuthMe() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setAuth = useAuthStore((s) => s.setAuth);
  const setRole = useAuthStore((s) => s.setRole);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setLoading = useAuthStore((s) => s.setLoading);

  const query = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: getAuthMe,
    enabled: !!accessToken,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Sync server state → auth store via effect (not during render)
  useEffect(() => {
    if (query.isSuccess && query.data) {
      const { refreshToken } = useAuthStore.getState();
      setAuth({
        user: query.data.user,
        organization: query.data.organization,
        tokens: { accessToken: accessToken!, refreshToken: refreshToken! },
        role: query.data.role,
      });
      setRole(query.data.role);
    }
    if (query.isError) {
      clearAuth();
    }
    if (query.isFetched) {
      setLoading(false);
    }
  }, [query.isSuccess, query.isError, query.isFetched, query.data, accessToken, setAuth, setRole, clearAuth, setLoading]);

  return query;
}

export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (data) => {
      setAuth({
        user: data.user,
        organization: data.organization,
        tokens: data.tokens,
      });
      navigate('/');
    },
  });
}

export function useRegister() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RegisterInput) => register(input),
    onSuccess: async (data) => {
      setAuth({
        user: data.user,
        organization: data.organization,
        tokens: data.tokens,
      });

      try {
        const conversations = await queryClient.fetchQuery({
          queryKey: ['conversations'],
          queryFn: getConversations,
        });
        const welcome =
          conversations.find((c) => c.threadType === 'ERIKO_INIT') ?? conversations[0];
        if (welcome) {
          navigate(`/chat/${welcome.id}`);
          return;
        }
      } catch {
        // Fall through to generic /chat fallback below.
      }

      navigate('/chat');
    },
  });
}

export function useLogout() {
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => logout(refreshToken!),
    onSettled: () => {
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => forgotPassword(email),
  });
}

export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: { token: string; password: string }) => resetPassword(input),
    onSuccess: () => {
      navigate('/login');
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => verifyEmail(token),
  });
}
